import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Output,
} from "@angular/core";
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { TranslateModule } from "@ngx-translate/core";
import { DropdownOption } from "src/app/components/ui-components/dropdown/dropdown.model";
import { DropdownComponent } from "../../../components/ui-components/dropdown/dropdown.component";
import { FormContainerComponent } from "../../../components/ui-components/forms/form-container/form-container.component";
import { FormInputComponent } from "../../../components/ui-components/forms/form-input/form-input.component";
import { FormTextareaComponent } from "../../../components/ui-components/forms/form-textarea/form-textarea.component";
import { FormInputType } from "../../../components/ui-components/forms/form-input/form-input.model";
import { FinancialDocumentBase } from "../financial-component-base";
import { DenominationItem, FinancialTransactionType, TransactionTypeCodes } from "src/app/services/model/create-financial-transaction.model";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { PaymentTypesService } from "src/app/services/payment-types.service";
import { IExecuteProcedureRequest, IExecuteProcedureResult, IGetCashMovementsSummaryRequest, IGetCashMovementsSummaryResponse, IGetPaymentTypesResultData } from "src/app/services/model/pos.api.model";
import { SharedModule } from "src/app/shared.module";
import { DbSchemeEnum } from "src/app/enums/db-scheme.enum";
import { ProcedureNameEnum } from "src/app/enums/procedure-name.enum";
import { SharedService } from "src/app/services/shared.service";

@Component({
  selector: "app-withdraw",
  templateUrl: "./withdraw.component.html",
  styleUrls: ["./withdraw.component.scss"],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropdownComponent,
    FormContainerComponent,
    FormInputComponent,
    FormTextareaComponent,
    TranslateModule,
    SharedModule,
    FontAwesomeModule,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WithdrawComponent extends FinancialDocumentBase {
    FormInputType = FormInputType;
    finDocType = FinancialTransactionType.PickupFromTill;
    valuableTypeOptions: DropdownOption[] = [];
    allPaymentTypes: IGetPaymentTypesResultData[] = [];
    selectedPaymentType: IGetPaymentTypesResultData | null = null;
    nominalOptions: DropdownOption[] = [];
    TenderType = TenderType;
    availableAmount: number | null = null;
    amountInsufficient: boolean = false;
    private cdr = inject(ChangeDetectorRef);
    private paymentTypesService = inject(PaymentTypesService);

    @Output() onClose = new EventEmitter<void>();

    private readonly withdrawalReasonCode = 'Withdrawal';

    form = new FormGroup({
        valuableType: new FormControl<string | null>(null, [Validators.required]),
        value: new FormControl<number | null>(null, [Validators.required]),
        reason: new FormControl<string>({ value: this.withdrawalReasonCode, disabled: true }, [Validators.required]),
        note: new FormControl<string>("", [Validators.required]),
        denominationRowsGroup: new FormGroup({
            rows: new FormArray([], Validators.nullValidator),
        }),
    });

    get rows(): FormArray {
        return this.form.controls.denominationRowsGroup.controls.rows as FormArray;
    }

    get hasNominals(): boolean {
        return this.selectedPaymentType?.TenderType !== TenderType.Cash &&
            !!this.selectedPaymentType?.Nominals?.length;
    }

    get totalNominalValue(): number {
        return this.rows.controls.reduce((total, row) => {
            const value = Number(row.get('value')?.value);
            const numberOfItems = Number(row.get('numberOfItems')?.value);
            if (!Number.isFinite(value) || !Number.isFinite(numberOfItems) || value <= 0 || numberOfItems <= 0) {
                return total;
            }
            return total + value * numberOfItems;
        }, 0);
    }

    get isSubmitDisabled(): boolean {
        if (!this.hasNominals) {
            return this.form.invalid || this.amountInsufficient;
        }
        return this.form.controls.valuableType.invalid ||
            this.form.controls.reason.invalid ||
            this.form.controls.note.invalid ||
            this.rows.length === 0 ||
            this.rows.invalid ||
            this.totalNominalValue <= 0 ||
            this.amountInsufficient;
    }

    async ngOnInit() {
        await this.loadData();

        // Filter to Withdrawal only
        this.reasonOptions = this.reasonOptions.filter(r => r.value === this.withdrawalReasonCode);
        
        const paymentTypes = await this.paymentTypesService.getPaymentTypesWithNominals();
        this.allPaymentTypes = paymentTypes.filter(pt => pt.TenderType === TenderType.Cash);
        this.valuableTypeOptions = this.allPaymentTypes.map(pt => ({
            value: pt.Name,
            label: pt.Description
        }));

        // Handle payment type changes: update nominals + available amount
        this.form.controls.valuableType.valueChanges.subscribe(async (paymentName) => {
            this.onPaymentTypeChange(paymentName);
            this.cdr.markForCheck();
            if (paymentName) {
                await this.loadAvailableAmount(paymentName);
            } else {
                this.availableAmount = null;
                this.amountInsufficient = false;
                this.cdr.markForCheck();
            }
        });

        // Auto-update value and check sufficiency when denomination rows change
        this.form.controls.denominationRowsGroup.valueChanges.subscribe(() => {
            if (this.hasNominals) {
                this.form.controls.value.setValue(this.totalNominalValue, { emitEvent: false });
                this.updateAmountInsufficient();
                this.cdr.markForCheck();
            }
        });

        // Re-evaluate insufficiency whenever the value changes (non-nominal case)
        this.form.controls.value.valueChanges.subscribe(() => {
            this.updateAmountInsufficient();
            this.cdr.markForCheck();
        });

        if (this.valuableTypeOptions.length === 1) {
            this.form.controls.valuableType.setValue(this.valuableTypeOptions[0].value);
        }
    }

    private onPaymentTypeChange(paymentName: string | null): void {
        this.selectedPaymentType = this.allPaymentTypes.find(pt => pt.Name === paymentName) || null;
        this.nominalOptions = (this.selectedPaymentType?.Nominals || []).map(n => ({
            value: n.value,
            label: n.denomination,
        }));

        while (this.rows.length > 0) {
            this.rows.removeAt(0);
        }

        if (this.hasNominals) {
            this.form.controls.value.setValue(0, { emitEvent: false });
            this.form.controls.value.disable({ emitEvent: false });
            this.form.controls.value.clearValidators();
            this.addRow();
        } else {
            this.form.controls.value.enable({ emitEvent: false });
            this.form.controls.value.setValidators([Validators.required]);
            this.form.controls.value.setValue(null, { emitEvent: false });
        }

        this.form.controls.value.updateValueAndValidity({ emitEvent: false });
    }

    addRow(): void {
        this.rows.push(new FormGroup({
            numberOfItems: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
            value: new FormControl<number | null>(null, [Validators.required]),
        }));

        const scrollContainer = document.getElementById('withdrawDenominationRows');
        if (scrollContainer) {
            setTimeout(() => scrollContainer.scrollTo(0, scrollContainer.scrollHeight), 0);
        }
    }

    removeRow(index: number): void {
        if (this.rows.length <= 1) {
            return;
        }
        this.rows.removeAt(index);
        this.form.controls.value.setValue(this.totalNominalValue, { emitEvent: false });
    }

    private async loadAvailableAmount(paymentName: string): Promise<void> {
        try {
            const jsonData = {
                CurrencyCode: this.loginService.user?.CurrencyCode,
                PaymentName: paymentName,
                ReturnToResult: 0,
            } as  IGetCashMovementsSummaryRequest;

            const request: IExecuteProcedureRequest<IGetCashMovementsSummaryRequest> = {
                DbScheme: DbSchemeEnum.Dert,
                ProcedureName: ProcedureNameEnum.GetCashMovementsSummary,
                Parameters: jsonData,
            };

            const result: IExecuteProcedureResult<IGetCashMovementsSummaryResponse> = await this.sharedApiService.executeProcedure(request);

            if (result?.data?.TotalMovement !== undefined && result.data.TotalMovement !== null) {
                this.availableAmount = result.data.TotalMovement;
            } else {
                this.availableAmount = null;
            }
        } catch (error) {
            console.error('Failed to load available amount for payment type', paymentName, error);
            this.availableAmount = null;
        } finally {
            this.updateAmountInsufficient();
            this.cdr.markForCheck();
        }
    }

    private updateAmountInsufficient(): void {
        const value = this.hasNominals
            ? this.totalNominalValue
            : Math.abs(this.form.controls.value.value ?? 0);
        this.amountInsufficient = this.availableAmount !== null && value > this.availableAmount;
    }

    async createFinancialTransaction() {
        if (this.isSubmitDisabled) {
            this.form.markAllAsTouched();
            return;
        }

        const selectedPaymentName = this.form.controls.valuableType.value;
        const note = this.form.controls.note.value;

        const selectedPaymentType = this.allPaymentTypes.find(pt => pt.Name === selectedPaymentName);
        const currencyCode = selectedPaymentType?.Currency || this.loginService.user.CurrencyCode;

        if (!selectedPaymentType) {
            console.error(`Selected payment type not found: ${selectedPaymentName}`);
            return;
        }

        const attributes: { [key: string]: string } = {
            'PaymentType': selectedPaymentName || '',
            'TenderType': selectedPaymentType.TenderType.toString() || '',
            'Note': note || ''
        };

        const denominationItems: DenominationItem[] = this.hasNominals
            ? this.rows.controls
                .map((row, index) => {
                    const value = Number(row.get('value')?.value);
                    const quantity = Number(row.get('numberOfItems')?.value);

                    if (!Number.isFinite(value) || !Number.isFinite(quantity) || value <= 0 || quantity <= 0) {
                        return null;
                    }

                    const nominal = selectedPaymentType.Nominals?.find(n => Number(n.value) === value);

                    return {
                        ItemIndex: index,
                        Caption: nominal?.denomination || value.toString(),
                        Value: value,
                        Quantity: quantity,
                        CurrencyISOCode: currencyCode,
                    } as DenominationItem;
                })
                .filter((item): item is DenominationItem => !!item)
            : [];

        const totalPrice = this.hasNominals
            ? this.totalNominalValue
            : Math.abs(this.form.controls.value.value || 0);

        await super.createFinancialTransaction(
            TransactionTypeCodes.PickupFromTill,
            selectedPaymentType.TenderType,
            selectedPaymentType.TenderSubType,
            totalPrice,
            "CASH_WITHDRAWAL",
            attributes,
            currencyCode,
            '',
            denominationItems,
        );
    }
}
